import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'components/Modals/Modal/Modal';
import Button from 'components/Button/Button';
import DndProvider from 'components/DragAndDrop/DndProvider/DndProvider';
import { DangerModal } from 'components/Modals/DangerModal/DangerModal';
import { Level, upLevel } from 'components/Modals/Modal/type';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import {
  IConfigurationQuestion,
  ICreateQuestionData,
} from 'types/ConfigurationQuestion.types';
import {
  useCreateConfigurationQuestion,
  useDeleteConfigurationQuestion,
  useGetConfigurationQuestions,
  useReorderConfigurationQuestions,
  useUpdateConfigurationQuestion,
} from '../hooks';
import { useReorderableList } from '../reorder';
import { QuestionRow } from '../QuestionRow/QuestionRow';
import { QuestionFormModal } from '../QuestionFormModal/QuestionFormModal';
import Draggable from 'components/DragAndDrop/Draggable/Draggable';
import {
  Container,
  EmptyState,
  HelperText,
  InlineButtonRow,
  List,
  SectionLabel,
  Title,
} from './QuestionsManager.styled';

interface IQuestionsManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  configurationId: number;
  modelName: string;
  currency: string;
  level: Level;
}

export const QuestionsManagerModal = ({
  isOpen,
  onClose,
  configurationId,
  modelName,
  currency,
  level,
}: IQuestionsManagerModalProps) => {
  const { t } = useTranslation();
  const { isPhone } = useBreakpointFlag();

  const { data: questions = [], isLoading } =
    useGetConfigurationQuestions(configurationId);

  const { mutate: createQuestion, isLoading: isCreating } =
    useCreateConfigurationQuestion(configurationId);
  const { mutate: updateQuestion } =
    useUpdateConfigurationQuestion(configurationId);
  const { mutate: deleteQuestion } =
    useDeleteConfigurationQuestion(configurationId);
  const { mutate: reorderQuestions } =
    useReorderConfigurationQuestions(configurationId);

  const { ordered, draggedId, setDraggedId, handleDragHover } =
    useReorderableList<IConfigurationQuestion>(questions, (ids) =>
      reorderQuestions({ question_ids: ids })
    );

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [questionForEditing, setQuestionForEditing] =
    useState<IConfigurationQuestion | null>(null);
  const [questionForDeletion, setQuestionForDeletion] =
    useState<IConfigurationQuestion | null>(null);
  const [expandedQuestionId, setExpandedQuestionId] = useState<number | null>(
    null
  );

  const closeForm = () => {
    setIsFormOpen(false);
    setQuestionForEditing(null);
  };

  const handleAdd = (data: ICreateQuestionData) => {
    createQuestion(data, { onSuccess: closeForm });
  };

  const handleEditLabel = (label: string) => {
    if (!questionForEditing) return;
    updateQuestion(
      { questionId: questionForEditing.id, data: { label } },
      { onSuccess: closeForm }
    );
  };

  return (
    <Modal
      level={level}
      isOpen={isOpen}
      onClose={onClose}
      modalStyle={{
        margin: 'auto',
        width: isPhone ? '95%' : '720rem',
        minWidth: isPhone ? '95%' : 'auto',
        maxHeight: '85vh',
        overflowY: 'auto',
        position: 'fixed',
      }}
    >
      <Container>
        <Title>
          {t('Manage extra price questions for {{MODEL_NAME}}').replace(
            '{{MODEL_NAME}}',
            modelName
          )}
        </Title>

        <SectionLabel>{t('Questions')}</SectionLabel>
        <HelperText>
          {t(
            'Activate a question to show it in the configurator. Drag to reorder.'
          )}
        </HelperText>

        <DndProvider>
          {isLoading ? (
            <EmptyState>{t('Loading')}...</EmptyState>
          ) : ordered.length ? (
            <List>
              {ordered.map((question, index) => (
                <Draggable
                  key={question.id}
                  type="question"
                  index={index}
                  id={question.id}
                  draggedId={draggedId}
                  setDraggedId={setDraggedId}
                  onDragHover={handleDragHover}
                  isDragDisabled={ordered.length < 2}
                >
                  <QuestionRow
                    question={question}
                    configurationId={configurationId}
                    currency={currency}
                    level={level}
                    isExpanded={expandedQuestionId === question.id}
                    onToggleExpand={() =>
                      setExpandedQuestionId((prev) =>
                        prev === question.id ? null : question.id
                      )
                    }
                    onToggleRequired={() =>
                      updateQuestion({
                        questionId: question.id,
                        data: { is_required: !question.is_required },
                      })
                    }
                    onToggleActive={() =>
                      updateQuestion({
                        questionId: question.id,
                        data: { is_active: !question.is_active },
                      })
                    }
                    onEdit={() => {
                      setQuestionForEditing(question);
                      setIsFormOpen(true);
                    }}
                    onDelete={() => setQuestionForDeletion(question)}
                  />
                </Draggable>
              ))}
            </List>
          ) : (
            <EmptyState>{t('No questions yet')}</EmptyState>
          )}
        </DndProvider>

        <InlineButtonRow>
          <Button
            primary
            label={t('Add question')}
            height="40rem"
            onClick={() => {
              setQuestionForEditing(null);
              setIsFormOpen(true);
            }}
          />
        </InlineButtonRow>
      </Container>

      <QuestionFormModal
        isOpen={isFormOpen}
        level={upLevel(level)}
        questionForEditing={questionForEditing}
        onClose={closeForm}
        onAdd={handleAdd}
        onEditLabel={handleEditLabel}
        isLoading={isCreating}
      />

      <DangerModal
        level={upLevel(level)}
        isOpen={!!questionForDeletion}
        setIsOpen={(open) => !open && setQuestionForDeletion(null)}
        title={t('Delete question')}
        description={t(
          'Are you sure you want to delete this question and all of its options?'
        )}
        submit={{
          onClick: () => {
            if (questionForDeletion) deleteQuestion(questionForDeletion.id);
            setQuestionForDeletion(null);
          },
          text: t('Confirm'),
          disabled: false,
        }}
        cancel={{ onClick: () => setQuestionForDeletion(null) }}
      />
    </Modal>
  );
};
